const debug = require('debug')('plugin:Server');
const xhr = require('xhr');
const qs = require('qs');
const isEmpty = require('lodash/isEmpty');
const get = require('lodash/get');

const util = require('./util');
const dom = require('./dom');
const MessageBus = require('./MessageBus');
const { parallel } = require('./util');
const ACTIONS = require('./const/actions');
const REQUEST_METHODS = require('./const/request-methods');
const csrf = require('public/js/lib/csrf');

const VALID_ORIGINS = [
	'https://revolutionparts.com',
	'https://revolutionparts.vpc',
];

module.exports = class PluginClient {
	constructor({window, clientWindow, env}) {
		debug('constructor', env);
		this.cache = {
			script: {},
			style: {},
		};
		this.clientWindow = clientWindow;
		this.window = window;
		this.pluginHash = env.pluginHash;
		this.stylesCache = {};
		this.globalCssRulesRegexp = this.getGlobalCssRuleRegexp();
		this.pluginHostDomain = util.trimSlash(rp_app.pluginHostDomain);
		this.openRequests = [];
		this.redirecting = false;

		// JWTA - Account-Level JWT
		this.accountJwt = null;
		window.addEventListener('beforeunload', function (e) {
			// If the user attempts to leave the site while logged in, they will lose their login session
			// This will alert the user, but you cannot set a custom message
			if (this.accountJwt && !this.redirecting) {
				// Send a message to the client
				// NOTE: this only gets displayed after the default browser message
				this.sendMessage({
					type: ACTIONS.unloadAccount,
				});

				// Cancel the event
				e.preventDefault();
				// If you prevent default behavior in Mozilla Firefox prompt will always be shown
				// Chrome requires returnValue to be set
				e.returnValue = undefined;
			}
		}.bind(this));


		const validOrigins = [
			...VALID_ORIGINS,
			this.pluginHostDomain,
		];

		this.bus = this.setupMessageBus({
			source: window,
			target: clientWindow,
			validOrigins,
		});

		this.bus.addMessageHandler(this.handleMessage.bind(this));

		// Tell client we're ready
		this.sendMessage({
			type: ACTIONS.ready,
			jwt: rp_app.jwt,
			baseUrl: `${this.pluginHostDomain}`,
		});
	}

	getGlobalCssRuleRegexp() {
		const globalCssRules = [
			'.modal-backdrop',
			'.fade',
			'.in',
			'.modal-open',
			'.popover', // Bootstrap Tether/Popover documents show `container` as an option... doesn't work. Still appends to body
			'.rp-modal-popover', // Same as .popover
		]
			.map((_rule) => {
				let rule = _rule.replace('.', '\\.');
				rule += '(?!\\w)';
				return rule;
			})
			.join('|');
		debug('getGlobalCssRuleRegexp', globalCssRules);
		return new RegExp(globalCssRules);
	}

	setupMessageBus({source, target, validOrigins}) {
		debug('setupMessageBus', /*source, */validOrigins);
		return new MessageBus({
			validOrigins,
			source,
			target,
		});
	}

	handleMessage({data={}, origin, source}) {
		debug('handleMessage', data, origin);
		const { body, jwt } = data;

		switch (data.type) {
			case ACTIONS.request:
				this.webstoreRequest({
					jwt,
					method: body.method,
					url: body.url,
					callback: this.webstoreRequestHandler.bind(this),
				});
				break;
			case ACTIONS.postRequest:
				this.webstoreRequest({
					jwt,
					method: body.method,
					url: body.url,
					body: body.data,
					callback: this.webstoreRequestHandler.bind(this),
				});
				break;
			case ACTIONS.jsonRequest:
				this.webstoreRequest({
					jwt,
					method: body.method,
					url: body.url,
					body: body.data,
					callback: this.jsonRequestHandler.bind(this),
				});
				break;
			case ACTIONS.abort:
				this.abortAllRequests();
				break;
			case ACTIONS.registerAccountJwt:
				this.accountJwt = get(body, 'ajwt');
				break;
			case ACTIONS.unregisterAccountJwt:
				this.accountJwt = null;
				break;
			case ACTIONS.startRedirect:
				this.redirectUrl(body.url, jwt);
				break;
		}
	}

	webstoreRequest({url:_url, method=REQUEST_METHODS.get, body={}, jwt, callback}) {
		debug('webstoreRequest', _url, method, body);
		if (!_url) return;
		if (!REQUEST_METHODS.has(method)) return;

		let url = _url;

		if (method === REQUEST_METHODS.get && !isEmpty(body.data)) {
			url = `${_url}?${qs.stringify(body.data)}`;
		}

		if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(method.toUpperCase())) {

			/* Make sure CSRF token is added to the request
			.*-----------------------------------------*/
			body.data = body.data || {};
			const csrfItems = csrf.getCsrfRequestData(csrf.getToken());
			csrfItems.forEach((item) => {
				Object.assign(body.data, item);
			});
		}

		const xhrObj = xhr({
			url,
			method,
			json: true,
			body: body.data,
			headers: {
				'X-RP-JWT': jwt,
				'X-RP-JWTA': this.accountJwt || null,
				'Content-Type': 'application/json',
			},
		}, this.xhrCallback(body.requestId, callback));

		this.openRequests.push(xhrObj);
	}

	xhrCallback(requestId, callback) {
		return (err, response, body) => {
			const xhrObj = response.rawRequest;
			callback(err, response, body, requestId);
			this.clearRequest(xhrObj);
			// Handle CSRF failure. We don't have page refreshes on plugin, therefore we need to refresh the iframe to get a new token
			if (response && response.statusCode === 401) {
				this.window.location = this.window.location;
			}
		};
	}

	abortAllRequests() {
		this.openRequests.forEach((req) => {
			if (req && req.abort) {
				req.abort();
				this.clearRequest(req);
			}
		});
		dom.abortAllRequests();
	}

	clearRequest(xhrObj) {
		if (xhrObj) {
			const index = this.openRequests.indexOf(xhrObj);
			if (this.openRequests[index]) {
				this.openRequests.splice(index, 1);
			}
		}
	}

	jsonRequestHandler(err, response={}, body={}, requestId) {
		debug(`jsonRequestHandler ${requestId} -> start`, err, response, body);
		const messages = [];

		if (err) {
			debug(`jsonRequestHandler ${requestId} -> ERROR`, err);
			messages.push(err);
		}

		const {statusCode, method, headers, url} = response;

		const updateClient = (message) => {
			this.updateClient(message);
			debug(`jsonRequestHandler ${requestId} -> end`);
		};

		if (body && headers) {
			body.rp_jwt = headers[`x-rp-jwt`];
		}

		updateClient({
			messages,
			statusCode,
			requestId,
			body,
			url,
			type: ACTIONS.clientJsonUpdate,
		});
	}

	webstoreRequestHandler(err, response={}, body={}) {
		debug('webstoreRequestHandler -> start', body);
		if (err) {
			return debug('webstoreRequestHandler -> ERROR', err);
		}

		const {statusCode, method, headers, url} = response;

		const updateClient = (message) => {
			this.updateClient(message);
			debug('webstoreRequestHandler -> end');
		};

		// Server gave us a string instead of JSON. Probably an error case.
		if (typeof body === 'string') {
			return updateClient({
				body: {
					html: body,
					scripts: [],
					styles: [],
				},
				url,
			});
		}

		if (body) {
			if (headers) {
				body.rp_jwt = headers[`X-RP-JWT`];
			}

			parallel({
				styles: (callback) => {
					if (Array.isArray(body.styles)) {
						this.getStyles(body.styles, callback);
					} else {
						callback(null, body.styles || []);
					}
				},
				scripts: (callback) => {
					if (Array.isArray(body.scripts)) {
						this.getScripts(body.scripts, callback);
					} else {
						callback(null, body.scripts || []);
					}
				},
			}, (err, result) => {
				debug('webstoreRequestHandler styles/scripts', err, result);
				body.scripts = result.scripts;
				body.styles = result.styles;
				updateClient({
					body,
					url,
				});
			});
		} else {
			updateClient({
				body,
				url,
			});
		}
	}

	updateClient(opts={}) {
		debug('updateClient', opts);
		if (!opts.type) {
			opts.type = ACTIONS.clientUpdate;
		}
		this.sendMessage(opts);
	}

	redirectUrl(baseUrl, jwt) {
		const params = [`jwt=${jwt}`];
		if (this.accountJwt) {
			params.push(`jwta=${this.accountJwt}`);
		}
		const redirectUrl = `${baseUrl}?${params.join('&')}`;
		this.redirecting = true;
		this.sendMessage({
			type: ACTIONS.redirect,
			url: redirectUrl,
		});
	}

	buildMessage(data) {
		return data;
	}

	sendMessage(message) {
		this.bus.send({
			message: this.buildMessage(message),
			origin: '*', // TODO - ugh
		});
	}

	getScripts(_scripts=[], callback) {
		if (_scripts.length === 0) {
			callback(null, []);
		}

		debug('getScripts - start', _scripts);

		const scriptsToLoad = _scripts
			.filter(script => !this.cache.script[script.script_src])
			.map((script) => {
				return {src: script.script_src, id: script.script_id};
			});

		dom.loadScripts(scriptsToLoad, (err, _result=[]) => {
			if (err) {
				return callback(err);
			}
			// Cache results
			_result.forEach((result) => {
				this.cache.script[result.src] = result;
			});

			// Build result with cache
			const result = _scripts.map((script) => {
				return this.cache.script[script.script_src];
			});

			callback(err, result);
		});
	}

	getStyles(_styles=[], callback) {
		if (_styles.length === 0) {
			callback(null, []);
		}

		debug('getStyles - start', _styles);

		const missingCssUrls = _styles
			.map(style => style.style_src)
			.filter((href) => {
				return !this.cache.style[href];
			});

		dom.loadStyles(missingCssUrls, (err, _links) => {
			if (err) {
				debug('getStyles - err', err);
				return callback(err);
			}

			const links = [];

			_styles.forEach((style) => {
				_links.forEach(({err, style:link}) => {
					if (this.cache.style[style.style_src]) {
						return;
					} else if (link.href === style.style_src) {
						const rules = dom.getStyleRulesFromLink(link);
						const styleContent = this.getClientStylesFromRules(rules);
						const newLink = {
							id: style.style_id,
							href: link.href,
							content: styleContent,
						};

						this.cache.style[style.style_src] = newLink;
					}
				});

				links.push(this.cache.style[style.style_src]);
			});

			debug('getStyles - done', links);
			callback(null, links);
		});
	}

	// Dear world: I'm sorry for this code
	// The goal here is to NOT need more code in webstore to handle plugin concerns. However, I think this
	// should be revisted and moved into webstore files (wrap all CSS with #rp_plugin or something) to lower our on-load CPU overhead
	getClientStylesFromRules(_rules) {
		return _rules.reduce((prev, _rule) => {
			// TODO - This ID needs to be a shared constant... or maybe dictated by the Client?
			const prefix = '#rp_plugin';
			// https://developer.mozilla.org/en-US/docs/Web/API/CSSRule
			if (_rule.type === 4) {
				// @media rule type -- Need to do a recursive rule dive if @media
				const rulesArray = dom.getRulesArrayFromRulesList(_rule.cssRules || _rule.rules);
				const modifiedRules = this.getClientStylesFromRules(rulesArray);
				let conditionText = _rule.conditionText;
				// SAFARI - Doesn't have conditionText property. Regex it.
				if (!conditionText) {
					conditionText = _rule.cssText.match(/^@media(.*)\{/i)[1];
				}
				return `${prev} @media ${conditionText} {${modifiedRules}}`;
			}

			let rule = _rule.cssText;
			// Don't mess with @font-face, @keyframes, or @keyframe rules
			if (_rule.type === 5 || _rule.type === 7 || _rule.type === 8) {
				return `${prev} ${rule}`;
			}
			// Ghetto performance improvement. Only do complex replacements on specific rule contents
			if (rule.indexOf(',') !== -1 || rule.indexOf('html') !== -1 || rule.indexOf('body') !== -1) {
				// We need to mutate the CSS selector to protect our page and the client's
				rule = rule.replace(/^(.*)\{(.*)$/, (match, p1, p2) => {
					// Filter out rules we do NOT want to have prefixed with plugin ID
					let selectorParts = p1.split(',').filter((_part) => {
						const part = _part.trim();
						return !this.globalCssRulesRegexp.test(part);
					});
					selectorParts = selectorParts.map(_part => {
						let part = _part.trim().toLowerCase();
						if (part === 'body' || part === 'html') {
							return '';
						}
						return _part;
					});
					const newSelector = (selectorParts.length) ? `${prefix} ${selectorParts.join(`,${prefix}`)}` : p1;
					return `${newSelector}{${p2}`;
				});
			} else if (rule.trim() === '*') {
				rule = `${prefix}, ${prefix} *`;
			} else if (this.globalCssRulesRegexp.test(rule)) {
				rule = `${rule} ${prefix} ${rule}`;
			} else {
				rule = `${prefix} ${rule}`;
			}
			return `${prev} ${rule}`;
		}, '');
	}
};
